import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiFileText, FiX, FiPlus, FiTrash2, FiCheckCircle, FiSlash } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const emptyLine = () => ({ description: '', net_amount: '', kdv_rate: '20.00' });
const emptyForm = () => ({
  entity: '', buyer_name: '', buyer_tax_id: '', buyer_address: '', issue_date: '', currency: '',
  receivable_account: '', revenue_account: '', kdv_payable_account: '', lines: [emptyLine()],
});

const SalesInvoices = () => {
  const { isFinanceLeadership } = useAuth();
  const [entities, setEntities] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, curRes, accRes, invRes] = await Promise.all([
        financeAPI.entities.list(), financeAPI.currencies.list(), financeAPI.accounts.list(), financeAPI.salesInvoices.list(),
      ]);
      setEntities(entRes.data.results || entRes.data || []);
      setCurrencies(curRes.data.results || curRes.data || []);
      setAccounts(accRes.data.results || accRes.data || []);
      setInvoices(invRes.data.results || invRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load sales invoices.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateLine = (idx, field, value) => {
    setForm((prev) => { const lines = [...prev.lines]; lines[idx] = { ...lines[idx], [field]: value }; return { ...prev, lines }; });
  };
  const addLine = () => setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }));
  const removeLine = (idx) => setForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form, lines: form.lines.filter((l) => l.description && l.net_amount) };
      await financeAPI.salesInvoices.create(payload);
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to create invoice.');
    } finally {
      setSaving(false);
    }
  };

  const doAction = async (inv, action) => {
    if (action === 'cancel') {
      const reason = window.prompt('Reason for cancelling this invoice?');
      if (!reason) return;
      setBusyId(inv.id);
      try { await financeAPI.salesInvoices.cancel(inv.id, reason); load(); }
      catch (e) { setError(e?.response?.data?.error || 'Failed to cancel invoice.'); }
      finally { setBusyId(null); }
      return;
    }
    setBusyId(inv.id);
    setError(null);
    try {
      await financeAPI.salesInvoices.issue(inv.id);
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to issue invoice.');
    } finally {
      setBusyId(null);
    }
  };

  const entityName = (id) => entities.find((e) => e.id === id)?.name || '—';
  const arAccounts = accounts.filter((a) => a.account_type === 'ASSET' && (!form.entity || String(a.entity) === String(form.entity)));
  const revAccounts = accounts.filter((a) => a.account_type === 'REVENUE' && (!form.entity || String(a.entity) === String(form.entity)));
  const liabAccounts = accounts.filter((a) => a.account_type === 'LIABILITY' && (!form.entity || String(a.entity) === String(form.entity)));

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiFileText size={22} />Sales Invoices</h1>
          <p style={fs.pageSubtitle}>e-Fatura/e-Arşiv-shaped, sequential no-gap numbering, KDV broken out per line.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Invoice</button>
          <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div><div style={fs.label}>Entity</div>
              <select required value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value, receivable_account: '', revenue_account: '', kdv_payable_account: '' })} style={{ ...fs.select, width: '100%' }}>
                <option value="">Select…</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div><div style={fs.label}>Buyer Name</div><input required value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
            <div><div style={fs.label}>Buyer Tax ID (TCKN/VKN)</div><input required value={form.buyer_tax_id} onChange={(e) => setForm({ ...form, buyer_tax_id: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
            <div><div style={fs.label}>Issue Date</div><input required type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
            <div><div style={fs.label}>Currency</div>
              <select required value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} style={{ ...fs.select, width: '100%' }}>
                <option value="">Select…</option>{currencies.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </div>
            <div><div style={fs.label}>Receivable Account (AR)</div>
              <select required value={form.receivable_account} onChange={(e) => setForm({ ...form, receivable_account: e.target.value })} style={{ ...fs.select, width: '100%' }}>
                <option value="">Select…</option>{arAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
              </select>
            </div>
            <div><div style={fs.label}>Revenue Account</div>
              <select required value={form.revenue_account} onChange={(e) => setForm({ ...form, revenue_account: e.target.value })} style={{ ...fs.select, width: '100%' }}>
                <option value="">Select…</option>{revAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
              </select>
            </div>
            <div><div style={fs.label}>KDV Payable Account</div>
              <select required value={form.kdv_payable_account} onChange={(e) => setForm({ ...form, kdv_payable_account: e.target.value })} style={{ ...fs.select, width: '100%' }}>
                <option value="">Select…</option>{liabAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
              </select>
            </div>
          </div>

          <div style={fs.label}>Lines</div>
          {form.lines.map((line, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input placeholder="Description" value={line.description} onChange={(e) => updateLine(idx, 'description', e.target.value)} style={{ ...fs.input, flex: 2 }} />
              <input type="number" step="0.01" placeholder="Net Amount" value={line.net_amount} onChange={(e) => updateLine(idx, 'net_amount', e.target.value)} style={{ ...fs.input, width: 130 }} />
              <input type="number" step="0.01" placeholder="KDV %" value={line.kdv_rate} onChange={(e) => updateLine(idx, 'kdv_rate', e.target.value)} style={{ ...fs.input, width: 90 }} />
              <button type="button" onClick={() => removeLine(idx)} style={{ ...fs.iconButton, padding: 8 }}><FiTrash2 size={13} /></button>
            </div>
          ))}
          <button type="button" onClick={addLine} style={{ ...fs.neutralButton, marginBottom: 16 }}><FiPlus size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Add Line</button>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Saving…' : 'Create Draft'}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : invoices.length === 0 ? (
        <div style={fs.emptyState}><FiFileText size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No sales invoices yet.</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {invoices.map((inv) => {
            const busy = busyId === inv.id;
            const color = fs.STATUS_COLORS[inv.status] || '#888';
            return (
              <div key={inv.id} style={fs.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ color: fs.colors.textFaint, fontSize: 12, fontFamily: 'monospace' }}>{inv.invoice_number || '(draft)'}</span>
                      <span style={{ color: fs.colors.textBright, fontWeight: 600, fontSize: 14 }}>{inv.buyer_name}</span>
                      <span style={fs.badge(color)}>{inv.status}</span>
                      <span style={{ color: fs.colors.textBright, fontWeight: 700 }}>{fs.money(inv.gross_total, inv.currency)}</span>
                    </div>
                    <div style={{ color: fs.colors.textFaint, fontSize: 12, marginTop: 4 }}>
                      {entityName(inv.entity)} · {inv.issue_date} · Net {inv.net_total} + KDV {inv.kdv_total}
                    </div>
                    {inv.status === 'CANCELLED' && inv.cancellation_reason && (
                      <div style={{ color: fs.colors.red, fontSize: 12, marginTop: 6 }}>Cancelled: {inv.cancellation_reason}</div>
                    )}
                  </div>
                  {isFinanceLeadership() && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      {inv.status === 'DRAFT' && (
                        <button onClick={() => doAction(inv, 'issue')} disabled={busy} style={fs.successButton}><FiCheckCircle size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Issue</button>
                      )}
                      {inv.status !== 'CANCELLED' && (
                        <button onClick={() => doAction(inv, 'cancel')} disabled={busy} style={fs.dangerButton}><FiSlash size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Cancel</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SalesInvoices;
